import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authApi from '../api/authApi';
import userApi from '../api/userApi';
import API_CONFIG from '../api/config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const storeAuthData = useCallback((authResponse) => {
    const { accessToken, refreshToken, userId, firstName, lastName, email, roles } = authResponse;
    localStorage.setItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    const userData = { userId, firstName, lastName, email, roles };
    localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER, JSON.stringify(userData));
    setUser(userData);
  }, []);

  const clearAuthData = useCallback(() => {
    localStorage.removeItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(API_CONFIG.STORAGE_KEYS.USER);
    setUser(null);
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const isTokenExpired = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 < Date.now();
      } catch {
        return true;
      }
    };

    const initAuth = async () => {
      try {
        const stored = localStorage.getItem(API_CONFIG.STORAGE_KEYS.USER);
        const token = localStorage.getItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);

        if (stored && token) {
          // If the token is expired, silently clear auth — don't make an API
          // call that would trigger a "Session expired" error on page load
          if (isTokenExpired(token)) {
            clearAuthData();
            return;
          }
          const userData = JSON.parse(stored);
          setUser(userData);

          // Try to refresh profile in background
          try {
            const response = await userApi.getProfile();
            if (response.data) {
              const profile = response.data;
              const updatedUser = {
                userId: profile.id,
                firstName: profile.firstName,
                lastName: profile.lastName,
                email: profile.email,
                roles: profile.roles,
                profileImageUrl: profile.profileImageUrl,
                bio: profile.bio,
                city: profile.city,
                state: profile.state,
                mobile: profile.mobile,
                emailVerified: profile.emailVerified,
                mobileVerified: profile.mobileVerified,
              };
              localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER, JSON.stringify(updatedUser));
              setUser(updatedUser);
            }
          } catch (err) {
            // Token might be expired or invalid
            if (err.status === 401 || err.status === 403) {
              clearAuthData();
            }
          }
        }
      } catch (err) {
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for forced logout from httpClient
    const handleLogout = () => {
      clearAuthData();
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [clearAuthData]);

  const login = async (credentials) => {
    const response = await authApi.login(credentials);
    storeAuthData(response.data);
    return response;
  };

  const signup = async (data) => {
    const response = await authApi.signup(data);
    storeAuthData(response.data);
    return response;
  };

  const loginWithOtp = async (data) => {
    const response = await authApi.verifyOtp(data);
    storeAuthData(response.data);
    return response;
  };

  const socialLogin = async (data) => {
    const response = await authApi.socialLogin(data);
    storeAuthData(response.data);
    return response;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      // Ignore errors on logout
    } finally {
      clearAuthData();
    }
  };

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const hasRole = useCallback(
    (role) => {
      return user?.roles?.includes(role) || false;
    },
    [user]
  );

  const isOrganizer = hasRole('ORGANIZER');
  const isAdmin = hasRole('ADMIN');

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isOrganizer,
    isAdmin,
    login,
    signup,
    loginWithOtp,
    socialLogin,
    logout,
    updateUser,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
